import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Shared layout for 404 / 403 / 500 system pages.
 *
 * Lifted from MES Design System bundle's `SystemPages.jsx`:
 * - centred card on dark surface (max-w 520px)
 * - 64×64 icon tile with tone-coloured glow
 * - mono uppercase eyebrow ("Помилка XXX")
 * - h1 title (sentence case) + body hint paragraph
 * - optional badge (role for 403, incident for 500)
 * - primary CTA + optional secondary support button
 *
 * Tone palette uses MES status tokens — neutral 404, warning 403,
 * destructive 500 — so the visual semantics match the rest of the app.
 */
type Tone = 'neutral' | 'warning' | 'danger'

interface SystemPageProps {
  code: '404' | '403' | '500'
  icon: LucideIcon
  title: string
  hint: string
  tone: Tone
  primaryAction: ReactNode
  secondaryAction?: ReactNode
  badge?: string
  embedded?: boolean
}

const TONE_TILE: Record<Tone, string> = {
  neutral: 'bg-secondary text-muted-foreground',
  warning: 'bg-status-issued-bg text-status-issued [box-shadow:0_0_18px_rgba(234,179,8,0.20)]',
  danger: 'bg-status-cancelled-bg text-destructive [box-shadow:0_0_18px_rgba(239,68,68,0.25)]',
}

const BADGE_TONE: Record<Tone, string> = {
  neutral: 'border-border bg-secondary text-muted-foreground',
  warning: 'border-status-issued/30 bg-status-issued-bg text-status-issued',
  danger: 'border-status-cancelled/30 bg-status-cancelled-bg text-status-cancelled',
}

export function SystemPage({
  code,
  icon: Icon,
  title,
  hint,
  tone,
  primaryAction,
  secondaryAction,
  badge,
  embedded = false,
}: SystemPageProps) {
  return (
    <div
      className={cn(
        'flex flex-1 items-center justify-center bg-background p-8',
        !embedded && 'min-h-screen',
      )}
    >
      <div
        className={cn(
          'w-full max-w-[520px] rounded-xl border border-border-strong bg-card p-9 text-center',
          'shadow-floating',
        )}
      >
        <div
          className={cn(
            'mx-auto mb-5 flex size-16 items-center justify-center rounded-xl',
            TONE_TILE[tone],
          )}
        >
          <Icon className="size-7" />
        </div>
        <div className="mb-2 font-mono text-2xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
          Помилка {code}
        </div>
        <h1 className="text-xl font-extrabold tracking-tight text-foreground">{title}</h1>
        <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{hint}</p>
        {badge && (
          <div
            className={cn(
              'mt-3.5 inline-block rounded-full border px-3 py-1.5 font-mono text-2xs',
              BADGE_TONE[tone],
            )}
          >
            {badge}
          </div>
        )}
        <div className="mt-6 flex justify-center gap-2">
          {primaryAction}
          {secondaryAction}
        </div>
      </div>
    </div>
  )
}
